import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";

import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Badge,
  Table,
  Spinner,
  Alert,
  Progress,
  Modal,
  ModalHeader,
  ModalBody,
  FormGroup,
  Label,
  Input,
} from "reactstrap";

import BreadCrumb from "../../Components/Common/BreadCrumb";
import { Elements } from '@stripe/react-stripe-js';
import { getStripe } from '../../helpers/stripe_helper';
import StripeUpgrade from './StripeUpgrade';
import PaymentMethodUpdate from './PaymentMethodUpdate';
// API helper
import { billingApi } from "../../helpers/billing_api_helper";

interface StripeInvoice {
  id: number;
  companyId: number;
  stripeInvoiceId: string;
  stripeCustomerId: string;
  stripeSubscriptionId: string;
  invoiceNumber: string;
  invoiceType: string;
  status: string;
  amountDue: number;
  amountPaid: number;
  currency: string;
  description: string;
  billingPeriodStart: string;
  billingPeriodEnd: string;
  invoicePdfUrl: string;
  hostedInvoiceUrl: string;
  paidAt: string;
  createdAt: string;
  updatedAt: string;
}

const Billing = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState<boolean>(true);
  const [apiError, setApiError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [billingData, setBillingData] = useState<any>(null);
  const [invoices, setInvoices] = useState<StripeInvoice[]>([]);
  const [invoicesLoading, setInvoicesLoading] = useState<boolean>(false);
  const [showStripeModal, setShowStripeModal] = useState<boolean>(false);
  const [showPaymentMethodModal, setShowPaymentMethodModal] = useState<boolean>(false);
  const [stripePromise, setStripePromise] = useState<any>(null);
  const [hardCap, setHardCap] = useState<boolean | null>(null);
  const [hardCapUpdating, setHardCapUpdating] = useState<boolean>(false);

  useEffect(() => {
    loadBillingData();
    loadInvoices();
    loadSubscriptionSettings();

    // Check for success message from upgrade page
    if (location.state?.message) {
      setSuccessMessage(location.state.message);
      // Clear the message from location state
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const loadBillingData = async () => {
    try {
      setLoading(true);
      setApiError(null);
      const response = await billingApi.getBillingData();
      setBillingData(response.data);
    } catch (error: any) {
      console.error('Error loading billing data:', error);
      setApiError('Failed to load billing data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadInvoices = async () => {
    try {
      setInvoicesLoading(true);
      const response = await billingApi.getInvoiceHistory();
      setInvoices(response.data || []);
    } catch (error: any) {
      console.error('Error loading invoices:', error);
    } finally {
      setInvoicesLoading(false);
    }
  };

  const loadSubscriptionSettings = async () => {
    try {
      const response = await billingApi.getSubscriptionSettings();
      if (response.data) {
        setHardCap(response.data.hardCap ?? true); // Default to true (hard cap) if null
      }
    } catch (error: any) {
      console.error('Error loading subscription settings:', error);
    }
  };

  const handleHardCapToggle = async () => {
    try {
      setHardCapUpdating(true);
      const newHardCap = !hardCap;
      const response = await billingApi.updateHardCap(newHardCap);
      if (response.data) {
        setHardCap(response.data.hardCap);
        setSuccessMessage(newHardCap
          ? "Hard cap enabled - usage will be blocked when limit is reached"
          : "Overage enabled - you will be billed for usage beyond your plan limit");
      }
    } catch (error: any) {
      console.error('Error updating hard cap:', error);
      setApiError('Failed to update overage setting. Please try again.');
    } finally {
      setHardCapUpdating(false);
    }
  };

  const handleUpgrade = () => {
    console.log('Upgrade button clicked');
    // Only load Stripe when user actually wants to upgrade
    if (!stripePromise) {
      setStripePromise(getStripe());
    }
    setShowStripeModal(true);
    console.log('Modal state set to true');
  };

  const handleUpgradeSuccess = () => {
    setShowStripeModal(false);
    setSuccessMessage("Successfully upgraded to Pro plan!");
    // Refresh billing data
    loadBillingData();
  };

  const handleUpgradeCancel = () => {
    setShowStripeModal(false);
  };

  const handleUpdatePaymentMethod = () => {
    // Only load Stripe when user actually wants to update payment method
    if (!stripePromise) {
      setStripePromise(getStripe());
    }
    setShowPaymentMethodModal(true);
  };

  const handlePaymentMethodUpdateSuccess = () => {
    setShowPaymentMethodModal(false);
    setSuccessMessage("Payment method updated successfully!");
    // Refresh billing data
    loadBillingData();
  };

  const handlePaymentMethodUpdateCancel = () => {
    setShowPaymentMethodModal(false);
  };

  const getPlanStatusBadge = (status: string) => {
    switch (status?.toLowerCase()) {
      case "trial":
        return <Badge color="primary" className="ms-2">TRIAL</Badge>;
      case "active":
        return <Badge color="success" className="ms-2">ACTIVE</Badge>;
      case "canceled":
        return <Badge color="warning" className="ms-2">CANCELED</Badge>;
      case "incomplete":
        return <Badge color="warning" className="ms-2">INCOMPLETE</Badge>;
      case "incomplete_expired":
      case "expired":
        return <Badge color="danger" className="ms-2">EXPIRED</Badge>;
      case "past_due":
        return <Badge color="danger" className="ms-2">PAST DUE</Badge>;
      case "unpaid":
        return <Badge color="danger" className="ms-2">UNPAID</Badge>;
      default:
        return <Badge color="secondary" className="ms-2">{status?.toUpperCase() || 'UNKNOWN'}</Badge>;
    }
  };

  const getInvoiceTypeBadge = (type: string) => {
    switch (type?.toLowerCase()) {
      case "subscription":
        return <Badge color="primary" className="me-1">Subscription</Badge>;
      case "overage":
        return <Badge color="warning" className="me-1">Overage</Badge>;
      case "proration":
        return <Badge color="info" className="me-1">Proration</Badge>;
      default:
        return <Badge color="secondary" className="me-1">{type || 'Other'}</Badge>;
    }
  };

  const getInvoiceStatusBadge = (status: string) => {
    switch (status?.toLowerCase()) {
      case "paid":
        return <Badge color="success" className="me-1">Paid</Badge>;
      case "open":
        return <Badge color="warning" className="me-1">Open</Badge>;
      case "draft":
        return <Badge color="secondary" className="me-1">Draft</Badge>;
      case "void":
        return <Badge color="dark" className="me-1">Void</Badge>;
      case "uncollectible":
        return <Badge color="danger" className="me-1">Uncollectible</Badge>;
      default:
        return <Badge color="secondary" className="me-1">{status || 'Unknown'}</Badge>;
    }
  };

  // Note: stripePromise is now initialized in handleUpgrade() to avoid loading Stripe on every page load

  document.title = "Billing & Subscription | Velzon - React Admin & Dashboard Template";
  
  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Billing & Subscription" pageTitle="Settings" />
          
          {apiError && <Alert color="danger">{apiError}</Alert>}
          {successMessage && <Alert color="success">{successMessage}</Alert>}

          {loading ? (
            <div className="text-center p-4">
              <Spinner color="primary" />
              <p className="mt-2 text-muted">Loading billing information...</p>
            </div>
          ) : (
            <>
              {/* Header */}
              <div className="d-flex flex-column gap-2 mb-4">
                <h4 className="mb-1">Billing & Subscription</h4>
                <p className="text-muted mb-0">Manage your plan, payment details, and invoices.</p>
              </div>

              {/* Cards Row */}
              <Row className="mb-4">
                {/* Current Plan Card */}
                <Col lg={6}>
                  <Card className="h-100">
                    <CardBody className="d-flex flex-column">
                      <div className="d-flex align-items-center justify-content-between mb-3">
                        <div className="d-flex align-items-center text-muted">
                          <i className="ri-file-list-2-line me-2"></i>
                          <small className="text-uppercase fw-bold">Current Plan</small>
                        </div>
                        {billingData?.currentSubscription?.status ? 
                          getPlanStatusBadge(billingData.currentSubscription.status) : 
                          <Badge color="secondary" className="ms-2">NO PLAN</Badge>
                        }
                      </div>
                      <div className="flex-grow-1">
                        <h5 className="mb-1">
                          {billingData?.currentSubscription ? 
                            billingData.currentSubscription.planName || 'Unknown Plan' : 
                            billingData?.companyInfo?.subscription || 'Free Plan'
                          }
                        </h5>
                        {billingData?.companyInfo?.subscriptionExpiryDate ? (
                          <small className="text-warning">
                            <i className="ri-time-line me-1"></i>
                            Expires on {new Date(billingData.companyInfo.subscriptionExpiryDate).toLocaleDateString()}
                            <br />
                            <span className="text-muted">Will automatically switch to Free Plan</span>
                          </small>
                        ) : billingData?.currentSubscription?.currentPeriodEnd ? (
                          <small className="text-muted">
                            Renews on {new Date(billingData.currentSubscription.currentPeriodEnd).toLocaleDateString()}
                          </small>
                        ) : billingData?.companyInfo?.trialEndDate ? (
                          <small className="text-muted">
                            Trial ends on {new Date(billingData.companyInfo.trialEndDate).toLocaleDateString()}
                          </small>
                        ) : (
                          <small className="text-muted">No active subscription</small>
                        )}
                      </div>
                      <div className="mt-3">
                        <Button color="primary" size="sm" className="d-flex align-items-center gap-1" onClick={handleUpgrade}>
                          <i className="ri-settings-3-line"></i>
                          Change Subscription
                        </Button>
                      </div>
                    </CardBody>
                  </Card>
                </Col>

                {/* Payment Method Card */}
                <Col lg={6}>
                  <Card className="h-100">
                    <CardBody className="d-flex flex-column">
                      <div className="d-flex align-items-center justify-content-between mb-3">
                        <div className="d-flex align-items-center text-muted">
                          <i className="ri-bank-card-line me-2"></i>
                          <small className="text-uppercase fw-bold">Payment Method</small>
                        </div>
                        <Button 
                          color="link" 
                          size="sm" 
                          className="p-0 text-primary fw-bold"
                          onClick={handleUpdatePaymentMethod}
                        >
                          Update
                        </Button>
                      </div>
                      <div className="flex-grow-1">
                        {billingData?.defaultCard ? (
                          <div className="d-flex align-items-center">
                            <div className="avatar-sm bg-light rounded me-3 d-flex align-items-center justify-content-center">
                              <i className="ri-bank-card-fill text-primary"></i>
                            </div>
                            <div>
                              <p className="mb-0 fw-bold">
                                {billingData.defaultCard.cardType ? 
                                  `${billingData.defaultCard.cardType.toUpperCase()} ending in ${billingData.defaultCard.cardLast4Digits}` : 
                                  'Payment Method'
                                }
                              </p>
                              <small className="text-muted">
                                Expires {billingData.defaultCard.cardExpiryMonth?.toString().padStart(2, '0')}/{billingData.defaultCard.cardExpiryYear}
                              </small>
                            </div>
                          </div>
                        ) : (
                          <div>
                            <p className="mb-0 text-muted">No payment method saved</p>
                            <small className="text-muted">Add a payment method to get started</small>
                          </div>
                        )}
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              </Row>

              {/* Usage Settings */}
              <Card className="mt-4">
                <CardBody>
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <h5 className="card-title mb-1">Usage Settings</h5>
                      <p className="text-muted mb-0">
                        {hardCap
                          ? "Your usage will be blocked when you reach your plan limit."
                          : "You can exceed your plan limit. Overage usage will be billed separately."}
                      </p>
                    </div>
                    <div className="d-flex align-items-center">
                      <FormGroup switch className="mb-0 me-2">
                        <Input
                          type="switch"
                          role="switch"
                          id="allowOverage"
                          checked={hardCap === false}
                          onChange={handleHardCapToggle}
                          disabled={hardCapUpdating || hardCap === null}
                        />
                        <Label check htmlFor="allowOverage" className="mb-0">
                          {hardCapUpdating ? (
                            <Spinner size="sm" className="me-1" />
                          ) : null}
                          Allow Overage
                        </Label>
                      </FormGroup>
                    </div>
                  </div>
                  {hardCap === false && (
                    <Alert color="warning" className="mt-3 mb-0">
                      <i className="ri-information-line me-2"></i>
                      Overage charges will be billed at the end of each billing period based on your plan's overage rate.
                    </Alert>
                  )}
                </CardBody>
              </Card>

              {/* Invoice History */}
              <Card className="mt-4">
                <CardBody>
                  <div className="mb-4">
                    <h5 className="card-title mb-0">Invoice History</h5>
                  </div>

                  {invoicesLoading ? (
                    <div className="text-center p-4">
                      <Spinner color="primary" size="sm" />
                      <p className="mt-2 text-muted mb-0">Loading invoices...</p>
                    </div>
                  ) : invoices && invoices.length > 0 ? (
                    <>
                      <div className="table-responsive">
                        <Table className="table-nowrap align-middle mb-0">
                          <thead className="table-light">
                            <tr>
                              <th scope="col">Date</th>
                              <th scope="col">Invoice #</th>
                              <th scope="col">Type</th>
                              <th scope="col">Amount</th>
                              <th scope="col">Status</th>
                              <th scope="col">Billing Period</th>
                              <th scope="col">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {invoices.map((invoice: StripeInvoice, index: number) => (
                              <tr key={invoice.id || index}>
                                <td>
                                  <div className="fw-medium">
                                    {invoice.paidAt ? new Date(invoice.paidAt).toLocaleDateString() :
                                     invoice.createdAt ? new Date(invoice.createdAt).toLocaleDateString() : '-'}
                                  </div>
                                </td>
                                <td>
                                  <div className="text-muted font-family-monospace">
                                    {invoice.invoiceNumber || invoice.stripeInvoiceId?.substring(0, 15) + '...'}
                                  </div>
                                </td>
                                <td>
                                  {getInvoiceTypeBadge(invoice.invoiceType)}
                                </td>
                                <td>
                                  <div className="fw-medium">
                                    ${invoice.amountPaid?.toFixed(2) || invoice.amountDue?.toFixed(2) || '0.00'} {invoice.currency?.toUpperCase() || 'USD'}
                                  </div>
                                </td>
                                <td>
                                  {getInvoiceStatusBadge(invoice.status)}
                                </td>
                                <td>
                                  {invoice.billingPeriodStart && invoice.billingPeriodEnd ? (
                                    <small className="text-muted">
                                      {new Date(invoice.billingPeriodStart).toLocaleDateString()} - {new Date(invoice.billingPeriodEnd).toLocaleDateString()}
                                    </small>
                                  ) : (
                                    <span className="text-muted">-</span>
                                  )}
                                </td>
                                <td>
                                  <div className="d-flex gap-2">
                                    {invoice.invoicePdfUrl && (
                                      <a
                                        href={invoice.invoicePdfUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="btn btn-sm btn-soft-primary"
                                        title="Download PDF"
                                      >
                                        <i className="ri-download-2-line"></i>
                                      </a>
                                    )}
                                    {invoice.hostedInvoiceUrl && (
                                      <a
                                        href={invoice.hostedInvoiceUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="btn btn-sm btn-soft-secondary"
                                        title="View Invoice"
                                      >
                                        <i className="ri-external-link-line"></i>
                                      </a>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>

                      <div className="d-flex align-items-center justify-content-between mt-3 pt-3 border-top">
                        <small className="text-muted">
                          Showing {invoices.length} invoice{invoices.length !== 1 ? 's' : ''}
                        </small>
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-5">
                      <div className="avatar-lg mx-auto mb-4">
                        <div className="avatar-title bg-light text-muted rounded-circle">
                          <i className="ri-bill-line fs-24"></i>
                        </div>
                      </div>
                      <h5 className="mb-2">No Invoices</h5>
                      <p className="text-muted">You don't have any invoices yet.</p>
                    </div>
                  )}
                </CardBody>
              </Card>
            </>
          )}
        </Container>
      </div>

      {/* Stripe Upgrade Modal */}
      <Modal isOpen={showStripeModal} toggle={handleUpgradeCancel} size="lg" centered>
        <ModalHeader toggle={handleUpgradeCancel}>
          Choose Your Plan
        </ModalHeader>
        <ModalBody>
          {showStripeModal && (
            <StripeUpgrade 
              stripePromise={stripePromise}
              onSuccess={handleUpgradeSuccess}
              onCancel={handleUpgradeCancel}
              currentPlanPriceId={billingData?.currentSubscription?.priceId}
              currentPlanName={billingData?.currentSubscription?.planName || billingData?.companyInfo?.subscription}
            />
          )}
        </ModalBody>
      </Modal>

      {/* Payment Method Update Modal */}
      <Modal isOpen={showPaymentMethodModal} toggle={handlePaymentMethodUpdateCancel} size="md" centered>
        <ModalHeader toggle={handlePaymentMethodUpdateCancel}>
          Update Payment Method
        </ModalHeader>
        <ModalBody>
          {showPaymentMethodModal && (
            <PaymentMethodUpdate 
              stripePromise={stripePromise}
              onSuccess={handlePaymentMethodUpdateSuccess}
              onCancel={handlePaymentMethodUpdateCancel}
            />
          )}
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default Billing;